/**
 * Application Store
 *
 * Store Zustand untuk menyimpan variabel-variabel aplikasi.
 *
 * PENTING: Nama variabel disamakan dengan Booku Lama (mdl_VariabelUmum.vb)
 * untuk memudahkan komparasi dan konsistensi.
 *
 * Penggunaan:
 *   const { NamaAplikasi, VersiAplikasi, TahunBukuAktif } = useAppStore();
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================
// KONSTANTA TANGGAL
// (mdl_VariabelUmum.vb:153-175)
// ============================================

/** Tanggal kosong standar - digunakan untuk field date yang tidak diisi */
export const TanggalKosong = '1900-01-01';

/** Tanggal tak terbatas - digunakan untuk batas maksimal */
export const TanggalTakTerbatas = '9999-01-01';

// ============================================
// KONSTANTA APLIKASI
// ============================================

/** Website aplikasi */
export const WebsiteAplikasi = 'booku.id';

/** Nama aplikasi */
export const NamaAplikasi = 'Booku';

/** Modus aplikasi: CLASSIC (MDI) atau MODERN (WPF Shell) - di V2 selalu MODERN */
export const ModusAplikasi = 'MODERN';

// ============================================
// INTERFACES
// ============================================

interface AppState {
  // ============================================
  // VARIABEL IDENTITAS APLIKASI
  // (mdl_VariabelUmum.vb:6-27)
  // ============================================

  /** Versi aplikasi (dari server/API) */
  VersiAplikasi: string;

  /** Build number aplikasi */
  ApdetAplikasi: string;

  /** Nomor hotline customer service */
  NomorHotLine: string;

  /** Email support aplikasi */
  EmailAplikasi: string;

  /** Judul aplikasi (kombinasi nama + tahun buku) */
  JudulAplikasi: string;

  // ============================================
  // VARIABEL TAHUN BUKU
  // (mdl_VariabelUmum.vb:163-201 & mdl_KoneksiDatabase.vb:60-68)
  // ============================================

  /** Tahun buku yang aktif saat ini */
  TahunBukuAktif: number;

  /** Tahun buku sebelumnya (TahunBukuAktif - 1). Dipakai untuk referensi saldo/pembayaran tahun lalu. */
  TahunBukuKemarin: number;

  /** Bulan buku aktif */
  BulanBukuAktif: number;

  /** Tanggal awal tahun buku aktif */
  AwalTahunBukuAktif: string | null;

  /** Tanggal akhir tahun buku aktif */
  AkhirTahunBukuAktif: string | null;

  /** Jenis tahun buku: NORMAL atau LAMPAU */
  JenisTahunBuku: 'NORMAL' | 'LAMPAU';

  /** Status buku: OPEN atau CLOSED */
  StatusBuku: 'OPEN' | 'CLOSED';

  /**
   * Sesi berjalan dalam MODE KARANTINA COA: tenant masih memakai COA format lama
   * (5 digit) sedangkan aplikasi sudah 9 digit. Hanya menu Migrasi COA 9 Digit yang
   * boleh dibuka — menu lain akan menampilkan angka nol yang menyesatkan.
   *
   * Penguncian sebenarnya ada di backend (`KarantinaCoaFilter`); flag ini dipakai
   * frontend untuk mengunci navigasi & menampilkan spanduk, bukan sebagai satu-satunya
   * pengaman.
   */
  KarantinaCoa: boolean;

  /** Apakah tahun buku aktif adalah tahun kabisat */
  TahunKabisat_TahunBukuAktif: boolean;

  // ============================================
  // VARIABEL TANGGAL SISTEM
  // (mdl_VariabelUmum.vb:153-175)
  // ============================================

  /** Tanggal hari ini (YYYY-MM-DD) */
  TanggalIni: string;

  /** Tanggal hari ini (angka 01-31) */
  TanggalHariIni: string;

  /** Bulan ini (angka 01-12) */
  BulanIni: string;

  /** Tahun ini (4 digit) */
  TahunIni: string;

  /** Tanggal akhir bulan ini (28, 29, 30, atau 31) */
  TanggalAkhirBulan: number;

  /** Apakah tahun ini kabisat */
  TahunKabisat_TahunIni: boolean;

  // ============================================
  // STATUS APLIKASI
  // (mdl_VariabelUmum.vb:189-205)
  // ============================================

  /** Apakah proses startup sudah selesai */
  StartupSelesai: boolean;

  /** Apakah aplikasi sedang loading */
  IsLoading: boolean;

  /** Apakah ada update tersedia */
  AdaUpdate: boolean;

  // ============================================
  // ACTIONS
  // ============================================

  /** Set info aplikasi dari API */
  setAppInfo: (info: Partial<AppState>) => void;

  /** Set tahun buku aktif */
  setTahunBuku: (tahun: number) => void;

  /** Update tanggal sistem (dipanggil saat startup) */
  updateTanggalSistem: () => void;

  /** Set status startup selesai */
  setStartupSelesai: (selesai: boolean) => void;

  /** Set loading state */
  setLoading: (loading: boolean) => void;

  /** Reset semua variabel (saat logout) */
  resetApp: () => void;

  // ============================================
  // CROSS-MODULE NAVIGATION HINTS
  // Pass transient data antar modul saat navigasi tanpa URL state.
  // Setter dipanggil oleh modul source sebelum `openTab`; modul target consume
  // via useEffect + reset ke null (one-shot).
  // ============================================

  /**
   * Filter bulan awal untuk halaman Buku Pengawasan Gaji.
   * Di-set oleh PPh 21 tab saat user klik "Detail Gaji dan Pesangon" di mode NORMAL —
   * padanan Booku Lama `usc_BukuPengawasanGaji.cmb_Bulan.SelectedValue = MasaPajak`.
   * Value: 'ALL' (REKAP) atau '1'-'12' (bulan spesifik).
   */
  gajiPageInitialBulan: string | null;

  setGajiPageInitialBulan: (bulan: string | null) => void;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/** Cek apakah tahun adalah kabisat */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/** Hitung tanggal akhir bulan */
function getLastDayOfMonth(year: number, month: number): number {
  // month adalah 1-12
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return daysInMonth[month - 1];
}

/** Format tanggal ke YYYY-MM-DD */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Get current date info */
function getCurrentDateInfo() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  return {
    TanggalIni: formatDate(now),
    TanggalHariIni: String(day).padStart(2, '0'),
    BulanIni: String(month).padStart(2, '0'),
    TahunIni: String(year),
    TanggalAkhirBulan: getLastDayOfMonth(year, month),
    TahunKabisat_TahunIni: isLeapYear(year),
  };
}

// ============================================
// DEFAULT VALUES
// ============================================

const defaultState = {
  // Identitas Aplikasi
  VersiAplikasi: '',
  ApdetAplikasi: '',
  NomorHotLine: '',
  EmailAplikasi: '',
  JudulAplikasi: NamaAplikasi,

  // Tahun Buku
  TahunBukuAktif: new Date().getFullYear(),
  TahunBukuKemarin: new Date().getFullYear() - 1,
  BulanBukuAktif: new Date().getMonth() + 1,
  AwalTahunBukuAktif: null,
  AkhirTahunBukuAktif: null,
  JenisTahunBuku: 'NORMAL' as const,
  StatusBuku: 'OPEN' as const,
  KarantinaCoa: false,
  TahunKabisat_TahunBukuAktif: isLeapYear(new Date().getFullYear()),

  // Tanggal Sistem
  ...getCurrentDateInfo(),

  // Status Aplikasi
  StartupSelesai: false,
  IsLoading: false,
  AdaUpdate: false,

  // Cross-module navigation hints (transient)
  gajiPageInitialBulan: null as string | null,
};

// ============================================
// STORE
// ============================================

export const useAppStore = create<AppState>()(
  persist(
    (set, _get) => ({
      ...defaultState,

      setAppInfo: (info) =>
        set((state) => {
          const merged = { ...state, ...info };
          // Auto-derive TahunBukuKemarin ketika TahunBukuAktif di-set via setAppInfo
          // agar selalu sinkron dengan TahunBukuAktif (= TahunBukuAktif - 1).
          if (info.TahunBukuAktif !== undefined && info.TahunBukuKemarin === undefined) {
            merged.TahunBukuKemarin = info.TahunBukuAktif - 1;
          }
          return merged;
        }),

      setTahunBuku: (tahun) =>
        set({
          TahunBukuAktif: tahun,
          TahunBukuKemarin: tahun - 1,
          TahunKabisat_TahunBukuAktif: isLeapYear(tahun),
          AwalTahunBukuAktif: `${tahun}-01-01`,
          AkhirTahunBukuAktif: `${tahun}-12-31`,
          JudulAplikasi: `${NamaAplikasi} - Tahun Buku ${tahun}`,
        }),

      updateTanggalSistem: () =>
        set({
          ...getCurrentDateInfo(),
        }),

      setStartupSelesai: (selesai) =>
        set({ StartupSelesai: selesai }),

      setLoading: (loading) =>
        set({ IsLoading: loading }),

      resetApp: () =>
        set({
          ...defaultState,
          ...getCurrentDateInfo(),
        }),

      setGajiPageInitialBulan: (bulan) =>
        set({ gajiPageInitialBulan: bulan }),
    }),
    {
      name: 'booku-app-storage',
      partialize: (state) => ({
        TahunBukuAktif: state.TahunBukuAktif,
        TahunBukuKemarin: state.TahunBukuKemarin,
        JenisTahunBuku: state.JenisTahunBuku,
        StatusBuku: state.StatusBuku,
        // WAJIB ikut di-persist. Tanpa ini, refresh halaman mengembalikannya ke
        // false sehingga sidebar tampil penuh & spanduk hilang, padahal backend
        // masih menolak (409) — user melihat menu yang ternyata tidak bisa dibuka.
        KarantinaCoa: state.KarantinaCoa,
      }),
    }
  )
);

// ============================================
// HELPER HOOKS
// ============================================

/**
 * Hook untuk mendapatkan tahun buku aktif
 */
export function useTahunBukuAktif(): number {
  return useAppStore((state) => state.TahunBukuAktif);
}

/**
 * Hook untuk mendapatkan tahun buku kemarin (TahunBukuAktif - 1).
 * Dipakai untuk referensi saldo/pembayaran tahun sebelumnya.
 */
export function useTahunBukuKemarin(): number {
  return useAppStore((state) => state.TahunBukuKemarin);
}

/**
 * Hook untuk mendapatkan tanggal hari ini
 */
export function useTanggalIni(): string {
  return useAppStore((state) => state.TanggalIni);
}

/**
 * Hook untuk cek apakah tahun buku aktif kabisat
 */
export function useTahunKabisat(): boolean {
  return useAppStore((state) => state.TahunKabisat_TahunBukuAktif);
}

/**
 * Hook untuk cek apakah startup sudah selesai
 */
export function useStartupSelesai(): boolean {
  return useAppStore((state) => state.StartupSelesai);
}
